import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, FilterIcon, DownloadIcon, MailIcon, EyeIcon, EditIcon, TrashIcon } from 'lucide-react';
import { Invoice } from '../types/Invoice';
export function Invoices() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Mock data - in a real app, this would come from an API
  const invoices: Invoice[] = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+94 77 123 4567',
    amount: 125000,
    currency: 'Rs.',
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    items: [],
    subtotal: 125000,
    total: 125000
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-002',
    customerName: 'Tech Solutions Ltd',
    customerEmail: 'accounts@techsolutions.lk',
    customerPhone: '+94 11 234 5678',
    amount: 87500,
    currency: 'Rs.',
    status: 'sent',
    dueDate: '2024-02-01',
    issueDate: '2024-01-15',
    description: 'Consulting services',
    items: [],
    subtotal: 87500,
    total: 87500
  }, {
    id: '3',
    invoiceNumber: 'INV-2024-003',
    customerName: 'Global Enterprises',
    customerEmail: 'finance@global.com',
    amount: 250000,
    currency: 'Rs.',
    status: 'overdue',
    dueDate: '2024-01-20',
    issueDate: '2024-01-05',
    description: 'Product delivery',
    items: [],
    subtotal: 250000,
    total: 250000
  }, {
    id: '4',
    invoiceNumber: 'INV-2024-004',
    customerName: 'Innovation Hub',
    customerEmail: 'billing@innovationhub.lk',
    amount: 45000,
    currency: 'Rs.',
    status: 'draft',
    dueDate: '2024-02-15',
    issueDate: '2024-01-25',
    description: 'Software license',
    items: [],
    subtotal: 45000,
    total: 45000
  }];
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-4236">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) || invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || invoice.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = filteredInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);
  const overdueAmount = filteredInvoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.amount, 0);
  return <div className="space-y-6" data-id="element-4237">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4238">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-4239">
          <div className="text-sm font-medium text-gray-500" data-id="element-4240">
            Total Invoices
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-4241">
            {filteredInvoices.length}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-4242">
          <div className="text-sm font-medium text-gray-500" data-id="element-4243">Total Amount</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-4244">
            Rs. {totalAmount.toLocaleString('en-LK')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-4245">
          <div className="text-sm font-medium text-gray-500" data-id="element-4246">Paid</div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-4247">
            Rs. {paidAmount.toLocaleString('en-LK')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-4248">
          <div className="text-sm font-medium text-gray-500" data-id="element-4249">Overdue</div>
          <div className="mt-2 text-3xl font-semibold text-red-600" data-id="element-4250">
            Rs. {overdueAmount.toLocaleString('en-LK')}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-4251">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-4252">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4253">
            <div className="flex-1 max-w-lg" data-id="element-4254">
              <div className="relative" data-id="element-4255">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-4256" />
                <input type="text" placeholder="Search invoices..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4257" />
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-4258">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4259">
                <option value="all" data-id="element-4260">All Status</option>
                <option value="draft" data-id="element-4261">Draft</option>
                <option value="sent" data-id="element-4262">Sent</option>
                <option value="paid" data-id="element-4263">Paid</option>
                <option value="overdue" data-id="element-4264">Overdue</option>
                <option value="cancelled" data-id="element-4265">Cancelled</option>
              </select>
              <button onClick={() => navigate('/invoice-form')} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-4266">
                <PlusIcon size={20} data-id="element-4267" />
                New Invoice
              </button>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-4268">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4269">
            <thead className="bg-gray-50" data-id="element-4270">
              <tr data-id="element-4271">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4272">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4273">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4274">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4275">
                  Issue Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4276">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4277">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4278">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4279">
              {filteredInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50" data-id="element-4280">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4281">
                    <div className="text-sm font-medium text-gray-900" data-id="element-4282">
                      {invoice.invoiceNumber}
                    </div>
                    {invoice.description && <div className="text-sm text-gray-500" data-id="element-4283">
                        {invoice.description}
                      </div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4284">
                    <div className="text-sm font-medium text-gray-900" data-id="element-4285">
                      {invoice.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4286">
                      {invoice.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4287">
                    <div className="text-sm font-medium text-gray-900" data-id="element-4288">
                      Rs. {invoice.amount.toLocaleString('en-LK')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4289">
                    {new Date(invoice.issueDate).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4290">
                    {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-4291">
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4292">
                    <div className="flex items-center justify-end gap-2" data-id="element-4293">
                      <button onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="text-blue-600 hover:text-blue-900" title="View" data-id="element-4294">
                        <EyeIcon size={18} data-id="element-4295" />
                      </button>
                      <button onClick={() => navigate(`/invoice-form?id=${invoice.id}`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-4296">
                        <EditIcon size={18} data-id="element-4297" />
                      </button>
                      <button className="text-blue-600 hover:text-blue-900" title="Send Email" data-id="element-4298">
                        <MailIcon size={18} data-id="element-4299" />
                      </button>
                      <button className="text-green-600 hover:text-green-900" title="Download" data-id="element-4300">
                        <DownloadIcon size={18} data-id="element-4301" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredInvoices.length === 0 && <div className="text-center py-12" data-id="element-4302">
            <p className="text-gray-500" data-id="element-4303">No invoices found</p>
          </div>}
      </div>
    </div>;
}